import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Target, CheckCircle2, DollarSign, ChevronDown, ChevronUp, Clock, Upload, UserPlus,
  AlertTriangle, Send, PauseCircle,
} from 'lucide-react';

const API = '/api';

// Leads Tracker — every Promote-Fitters lead, grouped by fitter, statused from
// follow_up_status + status_history (never the stale Meta field). Grant marks
// conversions here; fitters' one-tap "contacted" and Grant's "won" share one history.

const STATUS_STYLE = {
  new: 'border-hg-border text-hg-text-dim',
  contacted: 'border-blue-400/50 text-blue-300',
  booked: 'border-purple-400/50 text-purple-300',
  won: 'border-hg-green text-hg-green',
  lost: 'border-hg-border text-hg-text-dim line-through',
};

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ConversionControl({ lead, onChanged }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(lead.conversion?.amount ?? '');
  const [note, setNote] = useState(lead.conversion?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isWon = lead.follow_up_status === 'won';

  const save = async (converted) => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/leads-tracker/convert`, {
        lead_id: lead.id, converted,
        amount: amount === '' ? null : Number(amount), note: note || null,
      });
      setOpen(false); onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed.'); }
    setSaving(false);
  };

  if (isWon && !open) {
    return (
      <div className="flex items-center gap-2 text-[11px] font-sans">
        <span className="text-hg-green inline-flex items-center gap-1">
          <DollarSign size={11} /> Converted{lead.conversion?.amount ? ` — ${money(lead.conversion.amount)}` : ''}
          {lead.conversion?.note ? ` · “${lead.conversion.note}”` : ''}
        </span>
        <button onClick={() => setOpen(true)} className="text-hg-text-dim hover:text-hg-text underline">edit</button>
      </div>
    );
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
        data-testid={`convert-btn-${lead.id}`}>
        <DollarSign size={11} /> Converted to sale…
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px] font-sans" data-testid={`convert-form-${lead.id}`}>
      <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="1"
        className="w-24 bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-hg-text" placeholder="$ amount" />
      <input value={note} onChange={(e) => setNote(e.target.value)}
        className="w-48 bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-hg-text" placeholder="note (optional)" />
      <button onClick={() => save(true)} disabled={saving}
        className="px-2.5 py-1 bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
        {saving ? <Loader2 size={11} className="animate-spin" /> : 'Save sale'}
      </button>
      {isWon && (
        <button onClick={() => save(false)} disabled={saving}
          className="px-2.5 py-1 border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text">
          Un-mark
        </button>
      )}
      <button onClick={() => setOpen(false)} className="text-hg-text-dim hover:text-hg-text">cancel</button>
      {error && <span className="text-red-400">{error}</span>}
    </div>
  );
}

function LeadRow({ lead, onChanged }) {
  const [showHist, setShowHist] = useState(false);
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const st = lead.follow_up_status || 'new';

  // Record contact that happened OFF the Hub. Only offered while a lead is 'new'.
  // The note is required: a status with no story behind it is exactly how we lost the
  // ability to tell a worked lead from an abandoned one.
  const markContacted = async () => {
    if (!note.trim()) { setErr('A note is required — what actually happened?'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/leads/${lead.id}/mark-contacted`, { note });
      setNoting(false); setNote(''); onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not save.'); }
    setBusy(false);
  };

  return (
    <div className={`border border-hg-border/60 rounded-sm p-3 space-y-1.5 ${lead.is_test ? 'opacity-50' : ''}`}
         data-testid={`lead-${lead.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[13px] font-sans text-hg-text font-medium">
          {lead.name}{lead.is_test && <span className="text-hg-text-dim font-normal"> · TEST</span>}
          <span className="text-hg-text-dim font-normal text-[11px]"> · {String(lead.received || '').slice(0, 10)} · {lead.campaign}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-sans">
          {String(lead.source || '').startsWith('messenger') && (
            <span className="border border-sky-400/50 text-sky-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider"
              title={lead.meta_stage ? `Meta stage: ${lead.meta_stage}` : 'Messenger-sourced lead'}>
              messenger
            </span>
          )}
          {lead.source === 'website_form' && (
            <span className="border border-teal-400/50 text-teal-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider">
              website
            </span>
          )}
          {String(lead.source || '').startsWith('referral') && (
            <span className="border border-purple-400/50 text-purple-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider"
              title={lead.source}>
              referral
            </span>
          )}
          {/* Every other source gets its ACTUAL value shown. Before this, a lead whose
              source was e.g. 'direct_2026' or 'voicemail' carried no badge at all, so it
              read as a generic (Meta) lead purely because it lives in the meta_leads
              collection. Wrong attribution now becomes a lying report later. */}
          {lead.source && !String(lead.source).startsWith('messenger')
            && lead.source !== 'website_form' && !String(lead.source).startsWith('referral') && (
            <span className="border border-hg-border text-hg-text-dim rounded-sm px-1.5 py-0.5 uppercase tracking-wider"
              title={`source: ${lead.source}`}>
              {String(lead.source).replace(/_/g, ' ')}
            </span>
          )}
          {/* DISPLAY ONLY. The golfer's form ZIP sits further from the campaign's target
              postcode than the radius it was bought at — an ad-targeting fact, not a fact
              about the fitter, and it changes no routing. Opaque hex rather than a Tailwind
              -300 shade: this is a light surface (#FAFAF7) and the dark-theme scale the
              badges around it use renders near-invisible here. */}
          {lead.geo?.outside && (
            <span
              className="rounded-sm px-1.5 py-0.5 uppercase tracking-wider"
              style={{ color: '#8A5A00', backgroundColor: '#FDF3E0', border: '1px solid #E0B872' }}
              data-testid={`lead-geo-${lead.id}`}
              title={`Form ZIP ${lead.geo.lead_zip} is ~${lead.geo.miles} mi from the campaign target ${lead.geo.target_zip} (${lead.geo.radius} mi radius). Display only — routing is unchanged.`}>
              outside target area — ~{Math.round(lead.geo.miles)} mi
            </span>
          )}
          {lead.delivery !== 'not_applicable' && (
            <span className={`border rounded-sm px-1.5 py-0.5 uppercase tracking-wider ${lead.delivery === 'delivered' ? 'border-hg-green/60 text-hg-green' : 'border-amber-400/50 text-amber-300'}`}>
              {lead.delivery}
            </span>
          )}
          <span className={`border rounded-sm px-1.5 py-0.5 uppercase tracking-wider ${STATUS_STYLE[st] || STATUS_STYLE.new}`}>
            {st}
          </span>
        </div>
      </div>
      {st === 'new' && (
        noting ? (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`lead-note-${lead.id}`}>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} autoFocus
              placeholder="What happened? (called, emailed from Outlook, met in person…)"
              className="flex-1 min-w-[240px] bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-[11px] font-sans text-hg-text" />
            <button onClick={markContacted} disabled={busy} data-testid={`lead-mark-${lead.id}`}
              className="px-2.5 py-1 text-[11px] font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
              {busy ? '…' : 'Mark contacted'}
            </button>
            <button onClick={() => { setNoting(false); setErr(''); }}
              className="text-[11px] text-hg-text-dim hover:text-hg-text">cancel</button>
          </div>
        ) : (
          <button onClick={() => setNoting(true)} data-testid={`lead-mark-open-${lead.id}`}
            className="text-[11px] font-sans text-hg-text-dim hover:text-hg-green underline">
            Mark contacted…
          </button>
        )
      )}
      {err && <div className="text-[11px] text-red-400 font-sans">{err}</div>}
      {lead.contacted_note && (
        <div className="text-[11px] font-sans text-hg-text-dim" data-testid={`lead-contacted-${lead.id}`}>
          Contacted {String(lead.contacted_at || '').slice(0, 10)} by {lead.contacted_by || '—'}
          {lead.contacted_via ? ` (${lead.contacted_via})` : ''} — “{lead.contacted_note}”
        </div>
      )}
      {!lead.contacted_note && lead.contacted_via && lead.contacted_via !== 'manual' && (
        <div className="text-[11px] font-sans text-hg-text-dim">
          Contacted {String(lead.contacted_at || '').slice(0, 10)} via {lead.contacted_via}
          {lead.contacted_by ? ` (${lead.contacted_by})` : ''} — confirmed delivery
        </div>
      )}
      {lead.status_at && (
        <div className="text-[11px] font-sans text-hg-text-dim inline-flex items-center gap-1">
          <Clock size={10} /> {st} by {lead.status_actor || '—'} ({lead.status_source || '—'}) · {String(lead.status_at).slice(0, 16).replace('T', ' ')}
          {(lead.history || []).length > 1 && (
            <button onClick={() => setShowHist(!showHist)} className="ml-1 underline hover:text-hg-text">
              {showHist ? <ChevronUp size={10} className="inline" /> : <ChevronDown size={10} className="inline" />} history
            </button>
          )}
        </div>
      )}
      {showHist && (
        <div className="text-[10px] font-sans text-hg-text-dim bg-hg-bg/60 border border-hg-border rounded-sm p-2 space-y-0.5">
          {/* A 'note' entry carries status:null on purpose — the fitter told us something
              without claiming an outcome. Say "note", never a status it never had. */}
          {(lead.history || []).map((h, i) => (
            <div key={i}>
              {String(h.at).slice(0, 16).replace('T', ' ')} — {h.kind === 'note' ? 'note' : h.status} by {h.actor || '—'} via {h.source}
              {h.note && <span className="text-hg-text/70"> · {h.note}</span>}
            </div>
          ))}
        </div>
      )}
      {/* "Let us know" replies — the fitter's own words, straight off the nudge email.
          Shown separately from the import note, which is an artefact of how the lead got
          here rather than anything a person said. */}
      {(lead.fitter_notes || []).length > 0 && (
        <div className="space-y-1" data-testid={`lead-fitter-notes-${lead.id}`}>
          {(lead.fitter_notes || []).map((n, i) => (
            <div key={i} className="text-[11px] font-sans text-hg-text bg-hg-bg/40 border-l-2 border-hg-green rounded-sm p-2 whitespace-pre-line">
              “{n.text}”
              <span className="text-hg-text-dim"> — {n.actor || 'the fitter'}, {String(n.at || '').slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
      {lead.note && (
        <div className="text-[11px] font-sans text-hg-text-dim bg-hg-bg/40 border border-hg-border/60 rounded-sm p-2 whitespace-pre-line">
          {lead.note}
        </div>
      )}
      {(lead.next_step || lead.nudge_date) && (
        <div className={`text-[11px] font-sans ${lead.nudge_date && lead.nudge_date <= new Date().toISOString().slice(0, 10) ? 'text-amber-300' : 'text-hg-text-dim'}`}>
          {lead.next_step && <>Next: {lead.next_step} </>}
          {lead.nudge_date && <>· nudge {lead.nudge_date}</>}
        </div>
      )}
      {!lead.is_test && <ConversionControl lead={lead} onChanged={onChanged} />}
    </div>
  );
}

// The triage row the dashboard's "no follow-up at all, ≥3 days" chip deep-links to.
// Both actions are ONE click: the contacted note is prefilled (still editable in the
// full row below), and enrolling stages the drip PAUSED so the click is never the send.
function FirstTouchRow({ lead, onChanged }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const drip = lead.recruit_drip;

  const markContacted = async () => {
    setBusy('contacted'); setErr('');
    try {
      await axios.post(`${API}/leads/${lead.id}/mark-contacted`, {
        note: `Personal follow-up from the Leads Tracker triage list (${lead.days_waiting}d with no contact).`,
      });
      onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not save.'); }
    setBusy('');
  };

  const enrol = async () => {
    setBusy('drip'); setErr(''); setDone('');
    try {
      const res = await axios.post(`${API}/leads-tracker/${lead.id}/recruit-drip-enroll`);
      setDone(res.data?.note || 'Enrolled, paused.');
      onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not enrol.'); }
    setBusy('');
  };

  return (
    <div className="border border-amber-400/40 bg-hg-surface rounded-sm p-3 space-y-1.5"
      data-testid={`ft-row-${lead.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[13px] font-sans text-hg-text font-medium">
          {lead.name}
          <span className="text-hg-text-dim font-normal text-[11px]">
            {lead.email ? ` · ${lead.email}` : ''} · {lead.fitter}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-sans">
          <span className="border border-amber-400/50 text-amber-300 rounded-sm px-1.5 py-0.5 uppercase tracking-wider">
            {lead.days_waiting}d no contact
          </span>
          {lead.kind && (
            <span className="border border-hg-border text-hg-text-dim rounded-sm px-1.5 py-0.5 uppercase tracking-wider">
              {lead.kind}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-0.5">
        <button onClick={markContacted} disabled={!!busy} data-testid={`ft-contacted-${lead.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 disabled:opacity-40">
          {busy === 'contacted' ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
          Mark contacted
        </button>
        {drip ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-sans text-hg-text-dim"
            data-testid={`ft-drip-state-${lead.id}`}>
            <PauseCircle size={11} /> In recruit drip — {drip.status}
            {drip.step ? ` (step ${drip.step})` : ''}
          </span>
        ) : (
          <button onClick={enrol} disabled={!!busy} data-testid={`ft-enrol-${lead.id}`}
            title="Creates the enrolment PAUSED — nothing sends until you resume it in Drip Schedule"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green disabled:opacity-40">
            {busy === 'drip' ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Enrol in recruit drip (paused)
          </button>
        )}
      </div>
      {done && <div className="text-[11px] font-sans text-hg-green">{done}</div>}
      {err && <div className="text-[11px] font-sans text-red-400">{err}</div>}
    </div>
  );
}

export default function LeadsTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', source: 'referral', note: '' });
  const [adding, setAdding] = useState(false);
  // The dashboard chip deep-links with ?view=needs-first-touch. Read it ONCE at mount:
  // before this, the chip set the param and this screen ignored it, dropping you into all
  // 124 leads with the 8 it was pointing at buried in a 90-row fitter group.
  const [onlyFirstTouch, setOnlyFirstTouch] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('view') === 'needs-first-touch';
    } catch { return false; }
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/leads-tracker/list`);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load leads.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addLead = async () => {
    if (!addForm.name.trim()) { setError('Name is required.'); return; }
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/leads-tracker/add`, addForm);
      setAddOpen(false);
      setAddForm({ name: '', email: '', source: 'referral', note: '' });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not add the lead.');
    }
    setAdding(false);
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null); setError('');
    try {
      const text = await file.text();
      const res = await axios.post(`${API}/leads-tracker/import-leads-center`, { csv_text: text });
      setImportResult(res.data);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.error || 'Import failed.'));
    }
    setImporting(false);
  };

  const s = data?.summary || {};
  const firstTouch = (data?.groups || [])
    .flatMap((g) => g.leads)
    .filter((l) => l.needs_first_touch)
    .sort((a, b) => (b.days_waiting || 0) - (a.days_waiting || 0));
  return (
    <div className="space-y-4" data-testid="leads-tracker">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-xl text-hg-text inline-flex items-center gap-2">
            <Target size={18} className="text-hg-green" /> Leads Tracker
          </h1>
          <p className="text-[11px] font-sans text-hg-text-dim mt-0.5">
            Every Promote-Fitters lead, grouped by fitter. Fitters tap “Contacted”; mark sales here so we know what converts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="leads-add-btn" onClick={() => setAddOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green"
            title="Manually add a lead — referrals, phone calls, anyone who reached out off-platform">
            <UserPlus size={12} /> Add lead
          </button>
          <label className={`inline-flex items-center gap-1 text-[11px] font-sans cursor-pointer ${importing ? 'opacity-40' : 'text-hg-text-dim hover:text-hg-green'}`}
            title="Import a Meta Leads Center CSV export (Business Suite → Leads Center → export). Brings Messenger-sourced leads in with their stages. Never emails anyone.">
            {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import Leads Center CSV
            <input type="file" accept=".csv,text/csv" className="hidden" disabled={importing}
              onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>

      {addOpen && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-3 flex gap-2 flex-wrap items-center" data-testid="leads-add-form">
          <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
            placeholder="Full name *" className="w-44 bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs font-sans text-hg-text" />
          <input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
            placeholder="Email" className="w-52 bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs font-sans text-hg-text" />
          <input value={addForm.source} onChange={(e) => setAddForm({ ...addForm, source: e.target.value })}
            placeholder="Source (e.g. referral-sikorski)" className="w-48 bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs font-sans text-hg-text" />
          <input value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
            placeholder="Note" className="flex-1 min-w-[10rem] bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-xs font-sans text-hg-text" />
          <button onClick={addLead} disabled={adding}
            className="px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
            {adding ? <Loader2 size={12} className="animate-spin" /> : 'Add'}
          </button>
          <button onClick={() => setAddOpen(false)} className="text-[11px] font-sans text-hg-text-dim hover:text-hg-text">cancel</button>
          <div className="w-full text-[10px] font-sans text-hg-text-dim">
            Manually added leads are email-safe: no routing, no auto-reply, no drip — personal follow-up only.
          </div>
        </div>
      )}

      {importResult && (
        <div className="text-[11px] font-sans text-hg-text-dim border border-hg-border rounded-sm p-2">
          CSV import: {importResult.rows} rows — {importResult.created} new, {importResult.updated} updated,
          {' '}{importResult.unchanged} unchanged, {importResult.skipped} skipped.
        </div>
      )}
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[['Leads', s.total_leads],
              ['Contacted', `${s.contacted} (${s.contacted_rate}%)`],
              ['Converted', `${s.won} (${s.conversion_rate}%)`],
              ['Revenue', money(s.total_revenue)],
              ['Updated', String(data.generated_at || '').slice(11, 16) + ' UTC']].map(([k, v]) => (
              <div key={k} className="bg-hg-surface border border-hg-border rounded-sm p-3">
                <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">{k}</div>
                <div className="text-lg font-display text-hg-text">{v}</div>
              </div>
            ))}
          </div>

          {(s.junk_hidden || []).length > 0 && (
            <div className="text-[10px] font-sans text-hg-text-dim italic">
              {s.junk_hidden.length} suspected junk lead{s.junk_hidden.length > 1 ? 's' : ''} hidden:{' '}
              {s.junk_hidden.map((j) => j.name).join(', ')}
            </div>
          )}
          {(Object.keys(s.revenue_by_fitter || {}).length > 0) && (
            <div className="flex gap-4 flex-wrap text-[11px] font-sans text-hg-text-dim">
              <div><span className="uppercase tracking-wider text-[10px]">$ by fitter:</span>{' '}
                {Object.entries(s.revenue_by_fitter).map(([f, v]) => `${f} ${money(v)}`).join(' · ')}</div>
              <div><span className="uppercase tracking-wider text-[10px]">$ by campaign:</span>{' '}
                {Object.entries(s.revenue_by_campaign).map(([f, v]) => `${f} ${money(v)}`).join(' · ')}</div>
            </div>
          )}

          {firstTouch.length > 0 && (
            <div className="space-y-2" data-testid="first-touch-section">
              <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                <div className="text-[11px] font-sans uppercase tracking-wider text-amber-300 inline-flex items-center gap-1.5">
                  <AlertTriangle size={12} /> Needs a first touch ({firstTouch.length})
                </div>
                <button onClick={() => setOnlyFirstTouch((v) => !v)} data-testid="first-touch-toggle"
                  className="text-[11px] font-sans text-hg-text-dim hover:text-hg-green underline">
                  {onlyFirstTouch ? 'Show all leads' : 'Show only these'}
                </button>
              </div>
              <div className="text-[11px] font-sans text-hg-text-dim">
                Nothing has reached these people and no fitter owns them — the same rows the
                dashboard counts. Enrolling stages the drip <strong>paused</strong>; nothing sends
                until you resume it.
              </div>
              {firstTouch.map((l) => <FirstTouchRow key={l.id} lead={l} onChanged={load} />)}
            </div>
          )}

          {!onlyFirstTouch && (data.groups || []).map((g) => (
            <div key={g.fitter} className="space-y-2">
              <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim pt-1">
                {g.fitter} ({g.leads.length})
              </div>
              {g.leads.map((l) => <LeadRow key={l.id} lead={l} onChanged={load} />)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
